import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { all_routes } from '../../../router/all_routes'
import ImageWithBasePath from '../../../../core/common/imageWithBasePath'
import Table from "../../../../core/common/dataTable/index";
import AdminModal from '../adminModal';
import { RootState } from "../../../../core/data/redux/store";
import TooltipOption from "../../../../core/common/tooltipOption";
import { useDispatch, useSelector } from "react-redux";
import { fetchteachersSuccess } from "../../../../core/data/redux/teacherSlice";
import {
    fetchAdminApi,
    deleteAdminApi,
    getCurrentUserRoleType,
    getCurrentUserEntity
} from '../../../../core/data/services/adminService';
import { Admin } from '../../../../core/data/types/admin';
import {
    exportToPDF,
    exportToExcel,
    ExportColumn,
} from "../../../../utils/exportUtils";
import ErrorModal from '../../../../core/common/ErrorModal';
import SuccessModal from '../../../../core/common/successModal';
import CommonSelect, { Option } from '../../../../core/common/commonSelect';

// Define the filter interface
interface TeacherFilters {
    name: string;
    designation: string;
    email: string;
    phone: string;
}

// Initial filter state
const initialFilters: TeacherFilters = {
    name: '',
    designation: '',
    email: '',
    phone: '',
};

const AdminGrid = () => {
    const routes = all_routes;

    const hasFetched = useRef(false);
    const dispatch = useDispatch();
    const [deleteTeacherId, setDeleteTeacherId] = useState<number | null>(null);
    const [viewType,] = useState<"grid" | "list">("list");
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [currentUserRoleType, setCurrentUserRoleType] = useState<'admin' | 'state' | 'district' | 'block' | 'villageWard' | 'schoolHm' | null>(null);
    const [currentUserEntity, setCurrentUserEntity] = useState<any>(null);

    // Single filter state for all parameters
    const [filters, setFilters] = useState<TeacherFilters>(initialFilters);
    const [appliedFilters, setAppliedFilters] = useState<TeacherFilters>(initialFilters);
    const [filteredData, setFilteredData] = useState<Admin[]>([]);

    // Error and Success modal states
    const [error, setError] = useState<string>("");
    const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>("");
    const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);

    useEffect(() => {
        const userRoleType = getCurrentUserRoleType();
        const userEntity = getCurrentUserEntity();
        setCurrentUserRoleType(userRoleType);
        setCurrentUserEntity(userEntity);
        console.log("user entity is this in para: ", userEntity);

        // UPDATED: Allow village admins but not headmasters
        if (userRoleType === 'schoolHm') {
            setError("Unauthorized: Headmasters cannot access admin management");
            setShowErrorModal(true);
            return;
        }
    }, []);

    // Reusable function for role-based filtering
    const applyRoleBasedFiltering = useCallback((admins: Admin[]) => {
        if (currentUserRoleType === 'state' && currentUserEntity?.states) {
            return admins.filter((admin: Admin) => {
                const designation = admin.designation?.toLowerCase() || '';
                return designation.includes('district') || designation.includes('block');
            });
        } else if (currentUserRoleType === 'district' && currentUserEntity?.districts) {
            return admins.filter((admin: Admin) => {
                const designation = admin.designation?.toLowerCase() || '';
                return designation.includes('block') || designation.includes('village');
            });
        } else if (currentUserRoleType === 'block' && currentUserEntity?.blockAreas) {
            return admins.filter((admin: Admin) => {
                const designation = admin.designation?.toLowerCase() || '';
                return designation.includes('village') || designation.includes('ward');
            });
        } else if (currentUserRoleType === 'villageWard' && currentUserEntity?.villageWards) {
            // NEW: Village admin can only see headmasters
            return admins.filter((admin: Admin) => {
                const designation = admin.designation?.toLowerCase() || '';
                return designation.includes('headmaster') || designation.includes('hm');
            });
        } else if (currentUserRoleType === 'admin') {
            return admins.filter((admin: Admin) => {
                const designation = admin.designation?.toLowerCase() || '';
                return designation.includes('state') || designation.includes('district') || designation.includes('admin');
            });
        }
        return admins;
    }, [currentUserRoleType, currentUserEntity]);

    useEffect(() => {
        // UPDATED: Allow village admins
        if (hasFetched.current || !currentUserRoleType || currentUserRoleType === 'schoolHm') return;
        hasFetched.current = true;

        const fetchData = async () => {
            try {
                const addedadmin = await fetchAdminApi();
                const roleFilteredAdmins = applyRoleBasedFiltering(addedadmin);

                dispatch(fetchteachersSuccess(roleFilteredAdmins));
            } catch (error: any) {
                const msg = error?.message || "Failed to fetch admins.";
                setError(msg);
                setShowErrorModal(true);
                console.error("Error fetching admins:", msg);
            }
        };
        fetchData();
    }, [dispatch, currentUserRoleType, currentUserEntity, applyRoleBasedFiltering]);

    const teachers = useSelector((state: RootState) => state.teachers.teachers);
    useEffect(() => {
        setFilteredData(teachers);
    }, [teachers]);

    const updateFilter = (key: keyof TeacherFilters, value: string) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    // Generate dynamic filter options from actual data
    const getDynamicFilterOptions = () => {
        const nameOptions = [
            { value: '', label: 'All Names' },
            ...Array.from(new Set(teachers.map((teacher: any) =>
                `${ teacher.firstName } ${ teacher.lastName }`
            ))).map(name => ({ value: name, label: name }))
        ];

        const designationOptions = [
            { value: '', label: 'All Designations' },
            ...Array.from(new Set(teachers.map((teacher: any) =>
                teacher.designation
            ).filter(Boolean))).map(designation => ({
                value: designation,
                label: designation
            }))
        ];

        const emailOptions = [
            { value: '', label: 'All Emails' },
            ...Array.from(new Set(teachers.map((teacher: any) =>
                teacher.email
            ).filter(Boolean))).map(email => ({
                value: email,
                label: email
            }))
        ];

        return { nameOptions, designationOptions, emailOptions };
    };

    const applyFilters = useCallback((filtersToApply: TeacherFilters = appliedFilters) => {
        let filtered = teachers;

        if (filtersToApply.name) {
            filtered = filtered.filter((teacher: any) => {
                const fullName = `${ teacher.firstName } ${ teacher.lastName }`;
                return fullName.toLowerCase().includes(filtersToApply.name.toLowerCase());
            });
        }

        // Filter by designation
        if (filtersToApply.designation) {
            filtered = filtered.filter((teacher: any) =>
                teacher.designation === filtersToApply.designation
            );
        }

        if (filtersToApply.email) {
            filtered = filtered.filter((teacher: any) =>
                teacher.email.toLowerCase().includes(filtersToApply.email.toLowerCase())
            );
        }

        if (filtersToApply.phone) {
            filtered = filtered.filter((teacher: any) =>
                teacher.phone.includes(filtersToApply.phone)
            );
        }

        setFilteredData(filtered);
    }, [teachers, appliedFilters]);

    const resetFilters = () => {
        setFilters(initialFilters);
        setAppliedFilters(initialFilters);
        setFilteredData(teachers);
    };

    const hasActiveFilters = useCallback(() => {
        return Object.values(appliedFilters).some(value => value !== '');
    }, [appliedFilters]);

    const getActiveFilterCount = () => {
        return Object.values(appliedFilters).filter(value => value !== '').length;
    };

    useEffect(() => {
        if (!hasActiveFilters()) {
            setFilteredData(teachers);
        } else {
            applyFilters();
        }
    }, [teachers, applyFilters, hasActiveFilters]);

    const exportColumns: ExportColumn[] = [
        { title: "ID", dataIndex: "id" },
        {
            title: "Full Name",
            dataIndex: "firstName",
            render: (_, row) => `${ row.firstName } ${ row.lastName }`,
        },
        { title: "Designation", dataIndex: "designation" },
        { title: "Email", dataIndex: "email" },
        { title: "Phone", dataIndex: "phone" },
    ];

    const handleExportPDF = () =>
        exportToPDF(filteredData, exportColumns, "Admins.pdf");
    const handleExportExcel = () =>
        exportToExcel(filteredData, exportColumns, "Admins.xlsx");

    const handleConfirmDelete = async () => {
        if (deleteTeacherId == null) return;
        try {
            await deleteAdminApi(deleteTeacherId);
            const updatedAdmins = await fetchAdminApi();

            // Apply role-based filtering using the reusable function
            const roleFilteredAdmins = applyRoleBasedFiltering(updatedAdmins);

            dispatch(fetchteachersSuccess(roleFilteredAdmins));
            setSuccessMessage("Admin deleted successfully.");
            setShowSuccessModal(true);
        } catch (error: any) {
            const msg = error?.message || "An unknown error occurred during deletion.";
            console.error("Error deleting admin:", msg);
            setError(msg);
            setShowErrorModal(true);
        } finally {
            setDeleteTeacherId(null);
        }
    };

    const handleCloseErrorModal = () => {
        setShowErrorModal(false);
        setError("");
    };

    const handleCloseSuccessModal = () => {
        setShowSuccessModal(false);
        setSuccessMessage("");
    };

    const canEditOrDeleteAdmin = (admin: Admin) => {
        if (currentUserRoleType === 'admin') {
            const designation = admin.designation?.toLowerCase() || '';
            return !designation.includes('village') && !designation.includes('ward') && !designation.includes('headmaster');
        }

        if (currentUserRoleType === 'state' && currentUserEntity?.states) {
            const designation = admin.designation?.toLowerCase() || '';
            return designation.includes('district') || designation.includes('block');
        }

        if (currentUserRoleType === 'district' && currentUserEntity?.districts) {
            const designation = admin.designation?.toLowerCase() || '';
            return designation.includes('block') || designation.includes('village');
        }

        if (currentUserRoleType === 'block' && currentUserEntity?.blockAreas) {
            const designation = admin.designation?.toLowerCase() || '';
            return designation.includes('village') || designation.includes('ward');
        }

        if (currentUserRoleType === 'villageWard' && currentUserEntity?.villageWards) {
            // NEW: Village admin can edit/delete headmasters
            const designation = admin.designation?.toLowerCase() || '';
            return designation.includes('headmaster') || designation.includes('hm');
        }

        return false;
    };

    const getAddButtonConfig = () => {
        if (currentUserRoleType === 'admin') {
            return { text: 'Add Admin', show: true };
        } else if (currentUserRoleType === 'state') {
            return { text: 'Add District/Block Admin', show: true };
        } else if (currentUserRoleType === 'district') {
            return { text: 'Add Block Admin', show: true };
        } else if (currentUserRoleType === 'block') {
            return { text: 'Add Village Ward Admin', show: true };
        } else if (currentUserRoleType === 'villageWard') {
            return { text: 'Add Headmaster', show: true }; // NEW
        }
        return { text: 'Add Admin', show: false };
    };

    const getPageDescription = () => {
        if (currentUserRoleType === 'admin') {
            return 'Viewing all admins in the system (except village ward admins)';
        } else if (currentUserRoleType === 'state') {
            return 'Viewing district and block admins in your state';
        } else if (currentUserRoleType === 'district') {
            return 'Viewing block and village ward admins in your district';
        } else if (currentUserRoleType === 'block') {
            return 'Viewing village ward admins in your block area';
        } else if (currentUserRoleType === 'villageWard') {
            return 'Viewing headmasters in your village'; // NEW
        }
        return 'Admin management';
    };

    const dropdownMenuRef = useRef<HTMLDivElement | null>(null);

    const columns = [
        {
            title: "Admin Name & Designation",
            dataIndex: "firstName",
            render: (text: string, record: any) => (
                <div className="d-flex align-items-center">
                    <div className="avatar avatar-md">
                        <ImageWithBasePath
                            src={record.profileImage ?? "assets/img/teachers/teacher-01.jpg"}
                            className="img-fluid rounded-circle"
                            alt="img"
                        />
                    </div>
                    <div className="user-detail ms-2">
                        <p className="text-dark mb-0">{text} {record.lastName}</p>
                        <p className="mb-0">{record.designation}</p>
                    </div>
                </div>
            ),
            // sorter: (a: any, b: any) => a.firstName.localeCompare(b.firstName),
            sorter: (a: any, b: any) => {
                const fullNameA = `${ a.firstName } ${ a.lastName }`.toLowerCase();
                const fullNameB = `${ b.firstName } ${ b.lastName }`.toLowerCase();
                return fullNameA.localeCompare(fullNameB);
            },
        },
        {
            title: "Unique ID",
            dataIndex: "id",
            render: (_: any, record: any) => <span>{"UNIID" + record.id}</span>,
            sorter: (a: any, b: any) => a.id - b.id,
        },
        {
            title: "Email",
            dataIndex: "email",
            sorter: (a: any, b: any) => a.email.localeCompare(b.email),
        },
        {
            title: "Phone",
            dataIndex: "phone",
            sorter: (a: any, b: any) => a.phone.localeCompare(b.phone),
        },
        {
            title: "Admin Level",
            dataIndex: "designation",
            render: (designation: string) => {
                const level = designation?.toLowerCase() || '';
                let badgeClass = "badge badge-secondary";
                let levelText = "Unknown";

                if (level === 'admin') {
                    badgeClass = "badge badge-danger";
                    levelText = "Admin";
                } else if (level.includes('state')) {
                    badgeClass = "badge badge-warning";
                    levelText = "State";
                } else if (level.includes('district')) {
                    badgeClass = "badge badge-info";
                    levelText = "District";
                } else if (level.includes('block') && !level.includes('village')) {
                    badgeClass = "badge badge-success";
                    levelText = "Block";
                } else if (level.includes('village') || level.includes('ward')) {
                    badgeClass = "badge badge-primary";
                    levelText = "Village Ward";
                } else if (level.includes('headmaster') || level.includes('hm')) {
                    badgeClass = "badge badge-dark"; // NEW
                    levelText = "Headmaster";
                }

                return <span className={badgeClass}>{levelText}</span>;
            },
            sorter: (a: any, b: any) => a.designation.localeCompare(b.designation),
        },
        {
            title: "Action",
            dataIndex: "action",
            render: (_: any, record: Admin) => (
                <div className="dropdown">
                    <Link
                        to="#"
                        className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                    >
                        <i className="ti ti-dots-vertical fs-14" />
                    </Link>
                    <ul className="dropdown-menu dropdown-menu-right p-3">
                        <li>
                            <Link
                                className="dropdown-item rounded-1"
                                to={routes.adminDetails.replace(":id", record.id?.toString())}
                                state={{ teacher: record }}
                            >
                                <i className="ti ti-menu me-2" />View {currentUserRoleType === 'villageWard' ? 'Headmaster' : 'Admin'}
                            </Link>
                        </li>
                        {canEditOrDeleteAdmin(record) && (
                            <>
                                <li>
                                    <Link
                                        className="dropdown-item rounded-1"
                                        to={routes.editAdmin.replace(":id", record.id?.toString())}
                                        state={{ teacher: record }}
                                    >
                                        <i className="ti ti-edit-circle me-2" />Edit
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        className="dropdown-item rounded-1"
                                        to="#"
                                        data-bs-toggle="modal"
                                        data-bs-target="#delete-modal"
                                        onClick={() => setDeleteTeacherId(record.id)}
                                    >
                                        <i className="ti ti-trash-x me-2" />Delete
                                    </Link>
                                </li>
                            </>
                        )}
                        {!canEditOrDeleteAdmin(record) && (
                            <li>
                                <span className="dropdown-item text-muted">
                                    <i className="ti ti-lock me-2" />No Actions Available
                                </span>
                            </li>
                        )}
                    </ul>
                </div>
            ),
        },
    ];

    const handleApplyClick = () => {
        setAppliedFilters(filters);
        applyFilters(filters);

        if (dropdownMenuRef.current) {
            dropdownMenuRef.current.classList.remove("show");
        }
    };

    // Get dynamic options
    const { designationOptions } = getDynamicFilterOptions();

    const designationOpts: Option[] = useMemo(() => {
        // if options already in { value, label } shape, return as-is
        if (
            Array.isArray(designationOptions) &&
            designationOptions.length > 0 &&
            typeof designationOptions[0] === "object" &&
            "value" in designationOptions[0] &&
            "label" in designationOptions[0]
        ) {
            return designationOptions as Option[];
        }
        // if options are strings: ["Teacher", "Admin", ...]
        if (typeof designationOptions?.[0] === "string") {
            return (designationOptions).map((s) => ({ value: String(s), label: String(s) }));
        }
        // if options are objects with id/name: [{ id, name }]
        return (designationOptions as any[]).map((o) => ({
            value: String(o.id ?? o.value),
            label: String(o.name ?? o.label ?? o.text ?? o.title ?? o.id ?? ""),
        }));
    }, [designationOptions]);

    // 3) Derive the current Option from the string filter value
    const currentDesignation: Option | null = useMemo(() => {
        return filters.designation
            ? designationOpts.find((o) => String(o.value) === String(filters.designation)) ?? null
            : null;
    }, [filters.designation, designationOpts]);


    const addButtonConfig = getAddButtonConfig();
    if (!currentUserRoleType || currentUserRoleType === 'schoolHm') {
        return (
            <div className="page-wrapper">
                <div className="content content-two">
                    <div className="text-center">
                        <h3>Unauthorized Access</h3>
                        <p>You don't have permission to access admin management.</p>
                        <Link to={routes.adminDashboard} className="btn btn-primary">
                            Back to Dashboard
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Page Wrapper */}
            <div className="page-wrapper">
                <div className="content content-two">
                    {/* Page Header */}
                    <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
                        <div className="my-auto mb-2">
                            <h3 className="page-title mb-1">
                                {currentUserRoleType === 'admin' && 'Admin Management'}
                                {currentUserRoleType === 'state' && 'Admin Management'}
                                {currentUserRoleType === 'district' && 'Admin Management'}
                                {currentUserRoleType === 'block' && 'Village Admin Management'}
                                {currentUserRoleType === 'villageWard' && 'Headmaster Management'}
                            </h3>
                            <nav>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to={routes.adminDashboard}>Dashboard</Link>
                                    </li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        {currentUserRoleType === 'villageWard' ? 'Headmaster' : 'Admin'}
                                    </li>
                                </ol>
                            </nav>
                        </div>
                        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                            <TooltipOption
                                onExportPDF={handleExportPDF}
                                onExportExcel={handleExportExcel}
                            />

                            {addButtonConfig.show && (
                                <div className="mb-2">
                                    <Link
                                        to={routes.addAdmin}
                                        className="btn btn-primary d-flex align-items-center"
                                    >
                                        <i className="ti ti-square-rounded-plus me-2" />
                                        {addButtonConfig.text}
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
                            <div>
                                <h4 className="mb-1">
                                    {currentUserRoleType === 'villageWard' ? 'Headmaster List' : 'Admin List'}
                                </h4>
                                <p className="text-muted mb-3">
                                    {getPageDescription()}
                                </p>
                                <small className="text-info">
                                    Current Role: {currentUserRoleType?.charAt(0).toUpperCase() + currentUserRoleType?.slice(1)} Admin
                                    {currentUserEntity && (
                                        <span>
                                            {currentUserRoleType === 'state' && ` - ${ currentUserEntity.states?.name }`}
                                            {currentUserRoleType === 'district' && ` - ${ currentUserEntity.districts?.name }`}
                                            {currentUserRoleType === 'block' && ` - ${ currentUserEntity.blockAreas?.name }`}
                                            {currentUserRoleType === 'villageWard' && ` - ${ currentUserEntity.villageWards?.name }`}
                                        </span>
                                    )}
                                </small>
                            </div>
                            <div className="d-flex align-items-center flex-wrap">
                                <div className="dropdown mb-3 me-2">
                                    <Link
                                        to="#"
                                        className="btn btn-outline-light bg-white dropdown-toggle"
                                        data-bs-toggle="dropdown"
                                        data-bs-auto-close="outside"
                                    >
                                        <i className="ti ti-filter me-2" />
                                        Filter
                                        {hasActiveFilters() && (
                                            <span className="badge badge-primary ms-1">
                                                {getActiveFilterCount()}
                                            </span>
                                        )}
                                    </Link>
                                    <div
                                        className="dropdown-menu drop-width"
                                        ref={dropdownMenuRef}
                                    >
                                        <form>
                                            <div className="d-flex align-items-center border-bottom p-3">
                                                <h4>Filter {currentUserRoleType === 'villageWard' ? 'Headmaster' : 'Admin'}</h4>
                                            </div>
                                            <div className="p-3 pb-0 border-bottom">
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <div className="mb-3">
                                                            <label className="form-label">Name</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder={`Enter ${ currentUserRoleType === 'villageWard' ? 'headmaster' : 'admin' } name...`}
                                                                value={filters.name}
                                                                onChange={(e) => updateFilter('name', e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="mb-3">
                                                            <label className="form-label">Designation</label>
                                                            <CommonSelect
                                                                options={designationOpts}
                                                                defaultValue={currentDesignation}
                                                                onChange={(opt) => updateFilter("designation", opt?.value ?? "")}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="mb-3">
                                                            <label className="form-label">Email</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="Enter email..."
                                                                value={filters.email}
                                                                onChange={(e) => updateFilter('email', e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="mb-3">
                                                            <label className="form-label">Phone</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="Enter phone number..."
                                                                value={filters.phone}
                                                                onChange={(e) => updateFilter('phone', e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="p-3 d-flex align-items-center justify-content-between">
                                                <div className="text-muted small">
                                                    Showing {filteredData.length} of {teachers.length} {currentUserRoleType === 'villageWard' ? 'headmasters' : 'admins'}
                                                </div>
                                                <div>
                                                    <button
                                                        type="button"
                                                        onClick={resetFilters}
                                                        className="btn btn-light me-3"
                                                    >
                                                        Reset
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={handleApplyClick}
                                                        className="btn btn-primary"
                                                    >
                                                        Apply
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="">
                            {viewType === "list" && (
                                <div className="card-body p-0 py-3">
                                    <Table
                                        dataSource={filteredData}
                                        columns={columns}
                                        Selection={true}
                                        selectedRowKeys={selectedRowKeys}
                                        setSelectedRowKeys={setSelectedRowKeys}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {/* /Page Wrapper */}

            {/* Delete confirmation modal */}
            <AdminModal onConfirmDelete={handleConfirmDelete} />

            {/* Error Modal */}
            <ErrorModal
                show={showErrorModal}
                message={error}
                onClose={handleCloseErrorModal}
            />

            {/* Success Modal */}
            <SuccessModal
                show={showSuccessModal}
                message={successMessage}
                onClose={handleCloseSuccessModal}
            />
        </>
    );
};

export default AdminGrid;